import { useState } from 'react';
import { brokerCommissionRates } from '../data';

const verifiedDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long' });

export default function CommissionComparisonSection() {
  const [selectedBroker, setSelectedBroker] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<'commission' | 'minDeposit'>('commission');

  const sorted = [...brokerCommissionRates].sort((a, b) =>
    sortBy === 'commission' ? a.commissionRT - b.commissionRT : a.minDeposit - b.minDeposit
  );

  const toggleBroker = (key: string) => {
    setSelectedBroker(prev => (prev === key ? null : key));
  };

  return (
    <section className="w-full bg-background-100 py-12 md:py-18">
      <div className="max-w-main mx-auto px-4 md:px-6">

        {/* Section header */}
        <div className="max-w-[640px] mb-8 md:mb-16">
          <h2 className="text-[26px] md:text-[42px] font-[750] leading-[1.15] text-foreground-950 tracking-tight mb-3 md:mb-4">
            Real ECN commission rates compared
          </h2>
          <p className="text-[14px] md:text-[15px] leading-relaxed text-foreground-600">
            These are real, verified per-side rates from live accounts — not marketing numbers. Tap any row to see the fine print.
          </p>
        </div>

        {/* Sort toggle */}
        <div className="flex items-center gap-2.5 mb-5">
          <span className="text-[12px] md:text-[13px] text-foreground-500 flex-shrink-0">Sort by:</span>
          <div className="flex gap-2">
            {(['commission', 'minDeposit'] as const).map((opt) => (
              <button
                key={opt}
                onClick={() => setSortBy(opt)}
                className={`px-3.5 py-1.5 rounded-full text-[12px] md:text-[13px] font-[600] transition-all cursor-pointer whitespace-nowrap ${
                  sortBy === opt
                    ? 'bg-foreground-950 text-background-50'
                    : 'bg-background-50 text-foreground-600 border border-background-200 hover:border-foreground-300'
                }`}
              >
                {opt === 'commission' ? 'Commission (RT)' : 'Min Deposit'}
              </button>
            ))}
          </div>
        </div>

        {/* ── Mobile: card list ── */}
        <div className="sm:hidden space-y-3">
          {sorted.map((row) => {
            const key = `${row.broker}-${row.account}`;
            const isOpen = selectedBroker === key;
            const isLowCommission = row.commissionPerSide <= 3.0;

            return (
              <div
                key={key}
                className={`bg-background-50 rounded-xl border transition-all duration-200 overflow-hidden ${
                  isOpen ? 'border-accent-300' : 'border-background-200'
                }`}
              >
                {/* Card main row — tap to expand */}
                <button
                  type="button"
                  onClick={() => toggleBroker(key)}
                  className="w-full text-left px-4 py-4 cursor-pointer"
                >
                  {/* Top: broker name + badge + chevron */}
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[15px] font-[750] text-foreground-950">{row.broker}</span>
                        {row.highlight && (
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-[700] bg-accent-100 text-accent-700 whitespace-nowrap">
                            TOP PICK
                          </span>
                        )}
                      </div>
                      <span className="text-[12px] text-foreground-500 mt-0.5 block">{row.account}</span>
                    </div>
                    <i className={`ri-arrow-down-s-line w-5 h-5 flex items-center justify-center text-foreground-400 flex-shrink-0 transition-transform duration-200 mt-0.5 ${isOpen ? 'rotate-180' : ''}`} />
                  </div>

                  {/* Key metrics: Per Side + Round Turn */}
                  <div className="flex gap-3 mb-3">
                    <div className="flex-1 bg-background-100 rounded-lg px-3 py-2.5">
                      <span className="text-[10px] font-[650] text-foreground-400 uppercase tracking-wider block mb-1">Per Side</span>
                      <span className={`text-[22px] font-[800] leading-none tracking-tight ${isLowCommission ? 'text-accent-600' : 'text-foreground-950'}`}>
                        ${row.commissionPerSide.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex-1 bg-background-100 rounded-lg px-3 py-2.5">
                      <span className="text-[10px] font-[650] text-foreground-400 uppercase tracking-wider block mb-1">Round Turn</span>
                      <span className={`text-[22px] font-[800] leading-none tracking-tight ${row.commissionRT <= 6.0 ? 'text-accent-600' : 'text-foreground-950'}`}>
                        ${row.commissionRT.toFixed(2)}
                      </span>
                    </div>
                  </div>

                  {/* Secondary metadata */}
                  <div className="flex items-center gap-4 flex-wrap">
                    <div className="flex items-center gap-1.5">
                      <i className="ri-bank-card-line w-3.5 h-3.5 flex items-center justify-center text-foreground-400" />
                      <span className="text-[12px] text-foreground-500">Min ${row.minDeposit}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <i className="ri-bar-chart-box-line w-3.5 h-3.5 flex items-center justify-center text-foreground-400" />
                      <span className="text-[12px] text-foreground-500">{row.instruments}</span>
                    </div>
                    {!isOpen && (
                      <span className="text-[11px] text-accent-600 font-[600] ml-auto">Tap for details</span>
                    )}
                  </div>
                </button>

                {/* Expanded notes */}
                <div className={`grid transition-all duration-300 ${isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
                  <div className="overflow-hidden">
                    <div className="mx-4 mb-4 flex items-start gap-2 px-3.5 py-3 rounded-lg bg-accent-50 border border-accent-100">
                      <i className="ri-information-line w-4 h-4 flex items-center justify-center text-accent-500 mt-0.5 flex-shrink-0" />
                      <p className="text-[12px] leading-relaxed text-foreground-600">{row.notes}</p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* ── Desktop: table ── */}
        <div className="hidden sm:block bg-background-50 rounded-card border border-background-200 overflow-hidden">
          {/* Table header */}
          <div className="grid grid-cols-[1.5fr_1fr_0.8fr_0.8fr_0.9fr] gap-3 px-5 py-3.5 bg-background-100 border-b border-background-200 text-[12px] font-semibold text-foreground-500 uppercase tracking-wide">
            <span>Broker &amp; Account</span>
            <span>Per Side</span>
            <span>Round Turn</span>
            <span>Min Deposit</span>
            <span>Instruments</span>
          </div>

          {sorted.map((row) => {
            const key = `${row.broker}-${row.account}`;
            return (
              <div key={key}>
                <button
                  onClick={() => toggleBroker(key)}
                  className={`w-full grid grid-cols-[1.5fr_1fr_0.8fr_0.8fr_0.9fr] gap-3 px-5 py-4 text-left cursor-pointer transition-colors border-b border-background-100 last:border-b-0 hover:bg-background-100 ${
                    selectedBroker === key ? 'bg-accent-50' : ''
                  }`}
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-[14px] font-bold text-foreground-950">{row.broker}</span>
                      {row.highlight && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-accent-100 text-accent-700">TOP PICK</span>
                      )}
                    </div>
                    <span className="text-[12px] text-foreground-500">{row.account}</span>
                  </div>
                  <div className="flex items-center">
                    <span className={`text-[15px] font-bold ${row.commissionPerSide <= 3.0 ? 'text-accent-600' : 'text-foreground-950'}`}>
                      ${row.commissionPerSide.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <span className={`text-[15px] font-bold ${row.commissionRT <= 6.0 ? 'text-accent-600' : 'text-foreground-950'}`}>
                      ${row.commissionRT.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-[14px] font-semibold text-foreground-950">${row.minDeposit}</span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-[13px] text-foreground-600">{row.instruments}</span>
                  </div>
                </button>

                {selectedBroker === key && (
                  <div className="px-5 pb-4 bg-accent-50/50 border-b border-background-100">
                    <div className="flex items-start gap-2 pt-1">
                      <i className="ri-information-line w-4 h-4 flex items-center justify-center text-accent-500 mt-0.5 flex-shrink-0" />
                      <p className="text-[13px] leading-relaxed text-foreground-600">{row.notes}</p>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <p className="text-[11px] md:text-[12px] text-foreground-500 mt-3 text-center">
          Rates verified as of {verifiedDate}. Always check your broker&apos;s current contract specifications — rates change.
        </p>
      </div>
    </section>
  );
}